import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Text } from "@semcore/typography";
import Input from "@semcore/ui/input";
import { Box, Flex } from "@semcore/ui/flex-box";
import { useSelector } from "react-redux";
import { ZustandStore } from "../../../../../store/zustand-store";
import { languages } from "../../../../dataFiles/language";
import useFetchCountries from "../../../custom-hook/useFetchCountries";
import DropdownMenu from "../../../../../common/components/dropdown";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import SemButton from "../../../../../common/components/Button";
import AddProjectStore from "../../../../../store/AddProjectStore";
import { useTranslation } from "react-i18next";

import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import AddKeywordTag from "./AddKeywordTag";
import { LLMTrackerStore } from "../../../../../store/LLMTrackerStore";
import useDebouncedFunction from "../../../../../helperJs/debounce";

const AddWebsite = () => {
  const { t } = useTranslation();
  useFetchCountries();
  const [item, setItem] = useState([]);
  const [website, setWebsite] = useState({
    proj_name: "",
    domain: "",
    brand_name: [],
    llm_tracker: [],
    sel_type: "domain",
  });
  const [errors, setErrors] = useState({
    websiteURL: "",
    projName: "",
    brand_name: "",
    errorMsg: "",
    llm_tracker: "",
  });
  const [language, setLanguage] = useState({
    language_name: "English",
    language_code: "en",
  });
  const [selectedLocations, setSelectedLocations] = useState([]);
  const { addProject, setMultipleFields } = AddProjectStore((state) => state);
  const { country_Data, customer_plan } = ZustandStore((state) => state);
  const { USER_LLM_PROJECT } = LLMTrackerStore((state) => state);
  const accUsage = addProject.project_usage.limits;

  const userallprojectname = USER_LLM_PROJECT?.map(
    (project) => `${project.weburl}, ${project.language_code}`
  );
  const MAX_PROJECTS = customer_plan?.llm_project || 0;
  const CUR_PROJECTS = accUsage?.total_llm_project || 0;

  // Validation function
  const validate = () => {
    const newErrors = {
      websiteURL: "",
      projName: "",
      brand_name: "",
      llm_tracker: "",
    };
    const websiteURL = website.domain.trim();
    const domain = /^[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/i.test(websiteURL);
    if (!website.proj_name.trim())
      newErrors.projName = "Project Name is required!";
    if (!website.domain) newErrors.websiteURL = "Domain is required!";
    if (!domain) newErrors.websiteURL = "Please enter valid domain";
    if (item?.length === 0)
      newErrors.brand_name = "Add your Brand Name press Enter button";
    if (website.llm_tracker?.length === 0)
      newErrors.llm_tracker = "LLM is required!";
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  useEffect(() => {
    if (addProject.complete_steps?.length > 0) {
      setWebsite({
        proj_name: addProject.project_name,
        domain: addProject.domainUrl,
        brand_name: addProject.brand_name,
        llm_tracker: addProject.llm_tracker,
        sel_type: "domain",
      });
      setItem(addProject.brand_name);
      setLanguage(addProject.language);
      setSelectedLocations(addProject.location);
    }
  }, [addProject.complete_steps]);

  const langData = useMemo(() => {
    const lang = languages.map((lang) => lang.language);
    return {
      value: language.language_name,
      placeholder: "Select Language",
      disabled: false,
      data: lang,
      height: "38px",
      loading: false,
    };
  }, [languages, language.language_name]);

  const locData = useMemo(() => {
    const locationTypeOrder = [
      "Country",
      "State",
      "City",
      "Postal Code",
      "County",
      "District",
      "Neighborhood",
      "Municipality",
    ];
    const sortedLocations = country_Data?.data?.sort((a, b) => {
      const orderA = locationTypeOrder.indexOf(a.location_type);
      const orderB = locationTypeOrder.indexOf(b.location_type);
      return orderA - orderB;
    });
    const loc = sortedLocations?.map((loc) => {
      return {
        location_name: loc.location_name,
        location_type: loc.location_type,
      };
    });
    return {
      value: t("add_project.location_value"),
      placeholder: "Select Location",
      disabled: country_Data.loading || false,
      data: loc,
      height: "38px",
      type: "location",
      loading: country_Data.loading,
    };
  }, [country_Data]);

  const handleAddLanguage = useCallback(
    (value) => {
      const newLanguage = languages.find((lang) => lang.language === value);
      if (!newLanguage) return;
      setLanguage({
        language_name: newLanguage.language,
        language_code: newLanguage.languagecode,
      });
      setSelectedLocations([]);
    },
    [languages]
  );

  const handleAddLocation = useCallback(
    (value, type = "add") => {
      const newLocation = country_Data?.data?.find(
        (location) => location.location_name === value
      );
      if (!newLocation) return;
      setSelectedLocations((prev) => {
        const isPresent = prev.some(
          (item) => item.location_name === newLocation.location_name
        );
        if (isPresent && type === "add") return prev;
        if (isPresent) {
          // Remove it
          return prev.filter(
            (item) => item.location_name !== newLocation.location_name
          );
        } else {
          // Add it
          return [
            ...prev,
            {
              location_name: newLocation.location_name,
              location_code: newLocation?.location_code
                ? newLocation.location_code
                : newLocation.location_code_parent || "",
            },
          ];
        }
      });
    },
    [country_Data?.data]
  );

  const resetErrorByKey = useDebouncedFunction((key) => {
    setErrors((prevErrors) => {
      if (prevErrors[key]) {
        return {
          ...prevErrors,
          [key]: "",
        };
      }
      return prevErrors; // No change if the error value is already empty
    });
  }, 500); // debounce delay

  const handleSave = () => {
    if (!validate()) return; // Stop submission if errors exist
    const hasReachedLimit = CUR_PROJECTS >= MAX_PROJECTS;
    const curr_project = `${website.domain}, ${language.language_name}`;

    const setProjectError = (errorMsg) => {
      setErrors({
        websiteURL: "",
        projName: "",
        brand_name: "",
        llm_tracker: "",
        errorMsg,
      });
    };

    if (hasReachedLimit || selectedLocations?.length === 0) {
      const errorMsg = hasReachedLimit
        ? "You’ve reached your project limit. Upgrade your plan to add more."
        : "Please select at least one location.";
      setProjectError(errorMsg);
      return;
    }

    if (
      Array.isArray(userallprojectname) &&
      userallprojectname.includes(curr_project)
    ) {
      setProjectError(
        "This project already exists for this language. Please change the project language."
      );
      return;
    }

    setMultipleFields({
      type: "Add-Project",
      step: 2,
      domainUrl: website.domain,
      project_name: website.proj_name,
      brand_name: item,
      llm_tracker: website.llm_tracker,
      language: language,
      location: selectedLocations,
      complete_steps: ["1"],
    });
  };

  const handleCheckboxChange = (value) => {
    resetErrorByKey("llm_tracker");
    setWebsite((prev) => {
      const current = prev.llm_tracker || [];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value) // remove
        : [...current, value]; // add

      return { ...prev, llm_tracker: updated };
    });
  };

  const handleInputAddKeyword = useCallback(
    (input) => {
      // Normalize input to array and clean each keyword
      let keywordsToAdd = Array.isArray(input) ? input : [input];
      keywordsToAdd = keywordsToAdd.map((kw) => kw.trim()).filter(Boolean);

      // Remove duplicates within the input itself
      const normalizedInputSet = new Set();
      keywordsToAdd = keywordsToAdd.filter((kw) => {
        const lower = kw.toLowerCase();
        if (normalizedInputSet.has(lower)) return false;
        normalizedInputSet.add(lower);
        return true;
      });

      if (!keywordsToAdd.length) {
        return setErrors({
          type: "error",
          message: "No new brand name to add",
          show: true,
        });
      }

      setItem((prevTags) => {
        const existingLowerSet = new Set(prevTags.map((k) => k.toLowerCase()));
        const finalKeywords = keywordsToAdd.filter(
          (kw) => !existingLowerSet.has(kw.toLowerCase())
        );
        if (!finalKeywords.length) {
          setErrors((pre) => ({
            ...pre,
            brand_name: "This brand name is already exist",
          }));
          return prevTags;
        }

        if (errors?.brand_name) {
          setErrors((pre) => ({ ...pre, brand_name: "" }));
        }

        return [...prevTags, ...finalKeywords];
      });
    },
    [errors]
  );

  return (
    <Box style={{ padding: "0 3px" }}>
      <Flex direction="column" mt={2}>
        <Text tag="label" htmlFor="project-name" size={200}>
          {t("add_project.project_name")}
        </Text>
        <Input
          size="l"
          state={errors.projName ? "invalid" : "normal"}
          className="sem-input"
        >
          <Input.Value
            placeholder={t("add_project.project_name")}
            autoComplete="off"
            id="project-name"
            value={website.proj_name}
            onChange={(e) => {
              resetErrorByKey("projName");
              setWebsite((pre) => ({
                ...pre,
                proj_name: e,
              }));
            }}
          />
        </Input>
        {errors.projName && <Text color="red-500">{errors.projName}</Text>}
      </Flex>
      <Flex direction="column" my={2}>
        <Text tag="label" htmlFor="websiteURL" size={200}>
          Domain
        </Text>

        <Input
          size="l"
          state={errors.websiteURL ? "invalid" : "normal"}
          className="sem-input"
        >
          <Input.Value
            placeholder="Enter your domain"
            autoComplete="off"
            id="domain"
            value={website.domain}
            onChange={(e) => {
              resetErrorByKey("websiteURL");
              setWebsite((pre) => ({
                ...pre,
                domain: e,
              }));
            }}
          />
        </Input>
        {errors.websiteURL && <Text color="red-500">{errors.websiteURL}</Text>}
      </Flex>
      <Flex direction="column" my={2}>
        <Text tag="label" htmlFor="brand-name" size={200}>
          Brand Name
        </Text>
        <AddKeywordTag
          {...{
            item,
            setItem,
            handleInputAddKeyword,
            placeholder: "Type brand name and Press Enter Key",
          }}
        />
        {errors.brand_name && <Text color="red-500">{errors.brand_name}</Text>}
      </Flex>
      <Flex direction="column" my={2}>
        <Text tag="label" htmlFor="llm-tracker" size={200}>
          Select LLM
        </Text>

        <FormControl component="fieldset">
          <FormGroup aria-label="llm-tracker" row>
            {["gpt", "claude", "pp"].map((value) => (
              <FormControlLabel
                key={value}
                control={
                  <Checkbox
                    checked={website.llm_tracker.includes(value)}
                    onChange={() => handleCheckboxChange(value)}
                  />
                }
                label={
                  value === "gpt"
                    ? "ChatGPT"
                    : value === "claude"
                    ? "Claude"
                    : "Perplexity"
                }
              />
            ))}
          </FormGroup>
        </FormControl>
        {errors.llm_tracker && (
          <Text color="red-500">{errors.llm_tracker}</Text>
        )}
      </Flex>

      <Flex direction="column" mt={2}>
        <Text tag="label" htmlFor="project-name" size={200}>
          {t("add_project.language")}
        </Text>
        <DropdownMenu
          {...{
            menuData: langData,
            handleSelect: handleAddLanguage,
          }}
        />
      </Flex>
      <Flex direction="column" mt={2}>
        <Text tag="label" htmlFor="project-name" size={200}>
          {t("add_project.location")}
        </Text>
        <DropdownMenu
          {...{
            menuData: locData,
            handleSelect: handleAddLocation,
          }}
        />
        <Stack direction="row" spacing={1} my={1}>
          {selectedLocations?.map((location) => (
            <Chip
              key={location.location_name}
              label={location.location_name}
              size="small"
              className="heading-xs"
              onDelete={() =>
                handleAddLocation(location.location_name, "delete")
              }
              sx={{
                height: "20px",
                backgroundColor: "#ECEDF0",
                "& .MuiChip-label": {
                  fontWeight: "500",
                  fontFamily: "Work Sans",
                  color: `#6C6E79 !important`,
                },
              }}
            />
          ))}
        </Stack>
        {errors.errorMsg && <Text color="red-500">{errors.errorMsg}</Text>}
      </Flex>
      <Flex mt={4} flexWrap gap={3}>
        <SemButton
          size="m"
          ml={"auto"}
          name={t("common_buttons.next")}
          onClick={handleSave}
        />
      </Flex>
    </Box>
  );
};

export default AddWebsite;
